import React, { forwardRef, useCallback } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { TextInputField } from 'evergreen-ui';

import formatPhoneNumber from 'utils/formatePhoneNumber';

import styles from './Input.module.css';

const INPUT_TYPES = {
  TEXT: 'text',
  PASSWORD: 'password',
  PHONE_NUMBER: 'phoneNumber',
  NUMBER: 'number',
};

const Input = forwardRef(({
  value, onChange, type, name, label, placeholder, disabled,
  error, maxLength, onFocus, Icon, customIcon, iconPosition, className,
  pattern, description, onBlur, onKeyDown, margin,
}, ref) => {
  const getIcon = () => {
    if (customIcon) return customIcon;
    if (Icon) return <Icon className={styles.icon} />;
    return null;
  };

  const onValueChange = useCallback((e) => {
    const newValue = e.target.value;

    if (type === INPUT_TYPES.PHONE_NUMBER) {
      const formattedValue = formatPhoneNumber(newValue);

      onChange(formattedValue);
      return;
    }

    onChange(newValue, e.target.validity.valid);
  }, [onChange, type]);

  return (
    <div className={className}>
      <div className={cn(styles.inputWrapper, {
        [styles.error]: error,
        [styles.disabled]: disabled,
      })}
      >
        <TextInputField
          label={label}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={onValueChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          disabled={disabled}
          maxLength={maxLength}
          type={type}
          ref={ref}
          className={cn({
            [styles.error]: error,
            [styles.iconOnRight]: type === INPUT_TYPES.PASSWORD || Icon,
            [styles.iconOnLeft]: Icon && iconPosition === 'left',
            [styles.placeholder]: !value,
          }, styles.input)}
          isInvalid={!!error}
          validationMessage={error?.message || error}
          pattern={pattern}
          description={description}
          {...(margin !== null && { margin })}
        />
        <div className={cn({
          [styles.iconOnLeft]: iconPosition === 'left',
        }, styles.iconWrapper)}
        >
          {getIcon()}
        </div>
      </div>
    </div>
  );
});

Input.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  type: PropTypes.oneOf(Object.values(INPUT_TYPES)),
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.shape({
    message: PropTypes.string.isRequired,
  }),
  maxLength: PropTypes.number,
  onFocus: PropTypes.func,
  Icon: PropTypes.elementType,
  customIcon: PropTypes.element,
  iconPosition: PropTypes.string,
  className: PropTypes.string,
  pattern: PropTypes.string,
  description: PropTypes.string,
  margin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Input.defaultProps = {
  value: null,
  onChange: null,
  onBlur: null,
  onKeyDown: null,
  type: 'text',
  name: null,
  label: '',
  placeholder: null,
  disabled: false,
  error: null,
  maxLength: 150,
  onFocus: null,
  Icon: null,
  customIcon: null,
  iconPosition: 'right',
  className: null,
  pattern: null,
  description: null,
  margin: null,
};

export default Input;
